import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { renderNodeIcon, getNodeSolidColor } from "./nodeIcons";
import { useIconAccent } from "@/shared/lib/iconAccent";
import type { WorkflowNodeData } from "./types";

export default function WorkflowNodeCard({
  data,
  selected,
}: NodeProps<Node<WorkflowNodeData>>) {
  const nodeType = data.workflowNode.type;
  const iconUrl = data.descriptor?.icon;
  const { bg, shadow, text } = getNodeSolidColor(nodeType);
  const accent = useIconAccent(iconUrl, data.descriptor?.providerId ?? nodeType);
  const name = data.workflowNode.name || "Sin nombre";
  const subtitle = data.descriptor?.displayName ?? nodeType;

  return (
    <div className="group relative flex flex-col items-center gap-2.5">
      <Handle
        type="target"
        position={Position.Left}
        style={{ top: 40 }}
        className="!size-3 !bg-white !border-2 !border-white/80 !shadow-md !rounded-full !opacity-0 group-hover:!opacity-100 !transition-opacity"
      />

      {selected && (
        <div
          className="absolute pointer-events-none"
          style={{ top: 0, left: "50%", transform: "translateX(-50%)", width: 80, height: 80 }}
        >
          <div className="absolute -inset-1.5 rounded-[26px] ring-2 ring-accent/70 ring-offset-2 ring-offset-[#f0f0f3]" />
          <div className="absolute -inset-3 rounded-[32px] bg-accent/10 blur-md" />
        </div>
      )}

      <div
        className={`relative size-20 rounded-[22px] flex items-center justify-center shadow-xl transition-all duration-200 group-hover:scale-[1.06] group-hover:shadow-2xl ${
          iconUrl && accent ? "" : iconUrl ? "bg-white border border-black/5 shadow-black/10" : `${bg} ${shadow}`
        }`}
        style={
          iconUrl && accent
            ? {
                backgroundColor: `rgb(${accent})`,
                boxShadow: `0 12px 32px -6px rgba(${accent}, 0.5)`,
              }
            : undefined
        }
      >
        {data.isEntryNode && (
          <span className="absolute -top-1.5 -left-1.5 size-4 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
        )}
        <span className="size-12 rounded-2xl bg-white shadow-sm flex items-center justify-center overflow-hidden">
          {renderNodeIcon(nodeType, iconUrl ? "size-9" : `size-7 ${text}`, iconUrl)}
        </span>
      </div>

      <div className="text-center max-w-[110px]">
        <p className="text-[12px] font-semibold text-foreground/85 leading-tight truncate">{name}</p>
        <p className="text-[10px] text-foreground/40 leading-tight truncate">{subtitle}</p>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        style={{ top: 40 }}
        className="!size-3 !bg-white !border-2 !border-white/80 !shadow-md !rounded-full !opacity-0 group-hover:!opacity-100 !transition-opacity"
      />
    </div>
  );
}
